import React, { useState, useRef, useEffect } from 'react';

const LazyImage = ({
  src,
  alt,
  className = '',
  placeholder = 'bg-gray-200',
  fallback = '/images/placeholder.png',
  lqipSrc // New prop for low-quality image placeholder
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [error, setError] = useState(false);
  const imgRef = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px 0px' // Start loading 50px before the image comes into view
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const handleLoad = () => {
    setIsLoaded(true);
  };

  const handleError = () => {
    setError(true);
    setIsLoaded(true);
  };

  return (
    <div ref={imgRef} className={`relative overflow-hidden ${className}`}>
      {/* Low-quality image placeholder (LQIP) */}
      {lqipSrc && !isLoaded && (
        <img
          src={lqipSrc}
          alt={alt}
          className="w-full h-full object-cover absolute inset-0 blur-sm scale-110 transition-all duration-500"
          aria-hidden="true"
        />
      )}

      {/* Actual image */}
      {isInView && (
        <picture>
          <source srcSet={src.replace(/\.(png|jpg|jpeg)$/, '.webp')} type="image/webp" />
          <img
            src={error ? fallback : src}
            alt={alt}
            loading="lazy"
            decoding="async"
            className={`w-full h-full object-cover transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={handleLoad}
            onError={handleError}
          />
        </picture>
      )}
    </div>
  );
};

export default LazyImage;